import { useEffect, useRef, useState } from 'react';

type Variant = 'ornament' | 'branch' | 'floral';
interface Props { variant?: Variant; }

const G  = '#C9A050';
const GB = '#F5D860';
const GP = '#EDD070';

/* ─── IMPERIAL DIAMOND ─────────────────────────────────────────── */
function OrnamentSVG({ visible }: { visible: boolean }) {
  const draw = (delay: number, dur = 1.5): React.CSSProperties => ({
    strokeDasharray: 3000,
    strokeDashoffset: visible ? 0 : 3000,
    transition: visible ? `stroke-dashoffset ${dur}s cubic-bezier(0.4,0,0.2,1) ${delay}s` : 'none',
    opacity: visible ? 1 : 0,
  });
  const fade = (delay: number): React.CSSProperties => ({
    opacity: visible ? 1 : 0,
    transition: visible ? `opacity 0.5s ease ${delay}s` : 'none',
  });

  return (
    <svg width="100%" viewBox="0 0 600 80" style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id="imp-lg" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="transparent" />
          <stop offset="8%"   stopColor={G}  stopOpacity="0.6" />
          <stop offset="42%"  stopColor={GB} />
          <stop offset="58%"  stopColor={GB} />
          <stop offset="92%"  stopColor={G}  stopOpacity="0.6" />
          <stop offset="100%" stopColor="transparent" />
        </linearGradient>
        <filter id="imp-glow">
          <feGaussianBlur stdDeviation="3.5" result="b" />
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="imp-strong">
          <feGaussianBlur stdDeviation="6" result="b" />
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Main line glow layer */}
      <g filter="url(#imp-glow)" opacity="0.45">
        <path d="M15,40 H244"  stroke={GB} strokeWidth="5" fill="none" style={draw(0, 1.4)} />
        <path d="M356,40 H585" stroke={GB} strokeWidth="5" fill="none" style={draw(0, 1.4)} />
      </g>
      {/* Main lines */}
      <path d="M15,40 H244"  stroke="url(#imp-lg)" strokeWidth="2.2" fill="none" style={draw(0, 1.4)} />
      <path d="M356,40 H585" stroke="url(#imp-lg)" strokeWidth="2.2" fill="none" style={draw(0, 1.4)} />

      {/* Double chevrons — left */}
      <g filter="url(#imp-glow)">
        <path d="M196,22 L220,40 L196,58" stroke={GB} strokeWidth="3" fill="none" style={draw(0.4)} />
        <path d="M220,22 L244,40 L220,58" stroke={G}  strokeWidth="2" fill="none" style={draw(0.55)} />
      </g>
      {/* Double chevrons — right */}
      <g filter="url(#imp-glow)">
        <path d="M404,22 L380,40 L404,58" stroke={GB} strokeWidth="3" fill="none" style={draw(0.4)} />
        <path d="M380,22 L356,40 L380,58" stroke={G}  strokeWidth="2" fill="none" style={draw(0.55)} />
      </g>

      {/* Outer diamond — glow */}
      <g filter="url(#imp-glow)">
        <path d="M300,2 L356,40 L300,78 L244,40 Z" stroke={GB} strokeWidth="3" fill="none" style={draw(0.7, 1.3)} />
      </g>
      {/* Outer diamond — crisp */}
      <path d="M300,2 L356,40 L300,78 L244,40 Z" stroke={G} strokeWidth="2" fill="none" style={draw(0.7, 1.3)} />

      {/* Inner diamond */}
      <path d="M300,16 L334,40 L300,64 L266,40 Z" stroke={GP} strokeWidth="1.4" fill="none" style={draw(1.0)} />

      {/* Cross lines inside diamond */}
      <path d="M280,40 H320" stroke={G} strokeWidth="1" fill="none" style={draw(1.2, 0.7)} />
      <path d="M300,22 V58"  stroke={G} strokeWidth="1" fill="none" style={draw(1.2, 0.7)} />

      {/* Rotating 4-pointed star — center */}
      <g filter="url(#imp-strong)" style={fade(1.7)}>
        <g>
          <animateTransform attributeName="transform" type="rotate"
            from="0 300 40" to="360 300 40" dur="9s" repeatCount="indefinite" />
          <polygon points="300,24 304,36 300,48 296,36" fill={GB} opacity="0.9" />
          <polygon points="312,40 300,44 288,40 300,36" fill={GP} opacity="0.9" />
        </g>
        <circle cx="300" cy="40" r="5" fill={GB} />
      </g>

      {/* Twinkling accent dots */}
      {([
        [244,40, 6.5, 1.3],
        [356,40, 6.5, 1.3],
        [215,40, 4.5, 1.4],
        [385,40, 4.5, 1.4],
        [165,40, 3,   1.5],
        [435,40, 3,   1.5],
        [110,40, 2.5, 1.55],
        [490,40, 2.5, 1.55],
      ] as const).map(([cx, cy, r, d], i) => (
        <g key={i} filter={r > 4 ? "url(#imp-strong)" : "url(#imp-glow)"} style={fade(d)}>
          <circle cx={cx} cy={cy} r={r * 2} fill={GB} opacity="0.18">
            <animate attributeName="r"       values={`${r};${r*2.2};${r}`}   dur={`${2.2+i*0.35}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.08;0.3;0.08" dur={`${2.2+i*0.35}s`} repeatCount="indefinite" />
          </circle>
          <circle cx={cx} cy={cy} r={r} fill={GB}>
            <animate attributeName="r"       values={`${r*0.5};${r*1.4};${r*0.5}`} dur={`${2.2+i*0.35}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.5;1;0.5" dur={`${2.2+i*0.35}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}

      {/* Diamond tip dots */}
      <g filter="url(#imp-strong)" style={fade(1.55)}>
        <circle cx="300" cy="2"  r="3.5" fill={GB}>
          <animate attributeName="r" values="2;5;2" dur="2.8s" repeatCount="indefinite" />
        </circle>
        <circle cx="300" cy="78" r="3.5" fill={GB}>
          <animate attributeName="r" values="2;5;2" dur="2.8s" repeatCount="indefinite" begin="1.4s" />
        </circle>
      </g>
    </svg>
  );
}

/* ─── CELESTIAL RINGS ───────────────────────────────────────────── */
function BranchSVG({ visible }: { visible: boolean }) {
  const draw = (delay: number, dur = 1.5): React.CSSProperties => ({
    strokeDasharray: 3000,
    strokeDashoffset: visible ? 0 : 3000,
    transition: visible ? `stroke-dashoffset ${dur}s cubic-bezier(0.4,0,0.2,1) ${delay}s` : 'none',
    opacity: visible ? 1 : 0,
  });
  const fade = (delay: number): React.CSSProperties => ({
    opacity: visible ? 1 : 0,
    transition: visible ? `opacity 0.5s ease ${delay}s` : 'none',
  });

  return (
    <svg width="100%" viewBox="0 0 600 72" style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id="cel-lg" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="transparent" />
          <stop offset="10%"  stopColor={G}  stopOpacity="0.5" />
          <stop offset="40%"  stopColor={GB} />
          <stop offset="60%"  stopColor={GB} />
          <stop offset="90%"  stopColor={G}  stopOpacity="0.5" />
          <stop offset="100%" stopColor="transparent" />
        </linearGradient>
        <filter id="cel-glow">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="cel-strong">
          <feGaussianBlur stdDeviation="6" result="b" />
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Main line glow */}
      <g filter="url(#cel-glow)" opacity="0.4">
        <path d="M15,36 H262"  stroke={GB} strokeWidth="4.5" fill="none" style={draw(0, 1.3)} />
        <path d="M338,36 H585" stroke={GB} strokeWidth="4.5" fill="none" style={draw(0, 1.3)} />
      </g>
      {/* Main lines */}
      <path d="M15,36 H262"  stroke="url(#cel-lg)" strokeWidth="2" fill="none" style={draw(0, 1.3)} />
      <path d="M338,36 H585" stroke="url(#cel-lg)" strokeWidth="2" fill="none" style={draw(0, 1.3)} />

      {/* Leaf pairs — left */}
      <g filter="url(#cel-glow)">
        <path d="M158,36 C148,22 132,18 124,24 C130,30 144,34 158,36 Z" stroke={GB} strokeWidth="2" fill="none" style={draw(0.4)} />
        <path d="M158,36 C148,50 132,54 124,48 C130,42 144,38 158,36 Z" stroke={G}  strokeWidth="1.6" fill="none" style={draw(0.55)} />
        <path d="M200,36 C190,24 176,21 169,26 C175,31 188,35 200,36 Z" stroke={GB} strokeWidth="1.8" fill="none" style={draw(0.5)} />
        <path d="M200,36 C190,48 176,51 169,46 C175,41 188,37 200,36 Z" stroke={G}  strokeWidth="1.4" fill="none" style={draw(0.65)} />
      </g>
      {/* Leaf pairs — right */}
      <g filter="url(#cel-glow)">
        <path d="M442,36 C452,22 468,18 476,24 C470,30 456,34 442,36 Z" stroke={GB} strokeWidth="2"   fill="none" style={draw(0.4)} />
        <path d="M442,36 C452,50 468,54 476,48 C470,42 456,38 442,36 Z" stroke={G}  strokeWidth="1.6" fill="none" style={draw(0.55)} />
        <path d="M400,36 C410,24 424,21 431,26 C425,31 412,35 400,36 Z" stroke={GB} strokeWidth="1.8" fill="none" style={draw(0.5)} />
        <path d="M400,36 C410,48 424,51 431,46 C425,41 412,37 400,36 Z" stroke={G}  strokeWidth="1.4" fill="none" style={draw(0.65)} />
      </g>

      {/* Outer ring */}
      <g filter="url(#cel-glow)">
        <circle cx="300" cy="36" r="28" stroke={GB} strokeWidth="2.5" fill="none" style={draw(0.7, 1.2)} />
      </g>
      <circle cx="300" cy="36" r="28" stroke={G}  strokeWidth="1.6" fill="none" style={draw(0.7, 1.2)} />

      {/* Middle ring — counter-rotating */}
      <g filter="url(#cel-glow)" style={fade(1.0)}>
        <g>
          <animateTransform attributeName="transform" type="rotate"
            from="0 300 36" to="-360 300 36" dur="15s" repeatCount="indefinite" />
          <circle cx="300" cy="36" r="20" stroke={GP}  strokeWidth="1.2"
            fill="none" strokeDasharray="8 5" />
        </g>
      </g>

      {/* Inner ring */}
      <g style={fade(1.1)}>
        <g>
          <animateTransform attributeName="transform" type="rotate"
            from="0 300 36" to="360 300 36" dur="10s" repeatCount="indefinite" />
          <circle cx="300" cy="36" r="13" stroke={GB} strokeWidth="1.5"
            fill="none" strokeDasharray="5 4" />
        </g>
      </g>

      {/* Center dot cluster */}
      <g filter="url(#cel-strong)" style={fade(1.5)}>
        <circle cx="300" cy="36" r="5" fill={GB}>
          <animate attributeName="r" values="3;7;3" dur="2.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;1;0.7" dur="2.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="300" cy="36" r="2.5" fill="#FFFBE0" />
      </g>

      {/* 4 ring accent dots (on outer ring) */}
      {[[300,8],[328,36],[300,64],[272,36]].map(([cx, cy], i) => (
        <g key={i} filter="url(#cel-glow)" style={fade(1.3 + i * 0.1)}>
          <circle cx={cx} cy={cy} r="3.5" fill={GB}>
            <animate attributeName="r" values="2;5;2" dur={`${2+i*0.5}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}

      {/* Node dots on stems */}
      {([
        [240,36, 4, 1.25],[360,36, 4, 1.25],
        [195,36, 3, 1.35],[405,36, 3, 1.35],
        [148,36,2.5,1.42],[452,36,2.5,1.42],
        [100,36, 2, 1.48],[500,36, 2, 1.48],
      ] as const).map(([cx, cy, r, d], i) => (
        <g key={i} filter="url(#cel-glow)" style={fade(d)}>
          <circle cx={cx} cy={cy} r={r * 1.8} fill={GB} opacity="0.18">
            <animate attributeName="opacity" values="0.08;0.28;0.08" dur={`${2.5+i*0.3}s`} repeatCount="indefinite" />
          </circle>
          <circle cx={cx} cy={cy} r={r} fill={GB}>
            <animate attributeName="r"       values={`${r*0.6};${r*1.35};${r*0.6}`} dur={`${2.5+i*0.3}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.5;1;0.5" dur={`${2.5+i*0.3}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}
    </svg>
  );
}

/* ─── MANDALA BLOOM ─────────────────────────────────────────────── */
function FloralSVG({ visible }: { visible: boolean }) {
  const draw = (delay: number, dur = 1.4): React.CSSProperties => ({
    strokeDasharray: 3000,
    strokeDashoffset: visible ? 0 : 3000,
    transition: visible ? `stroke-dashoffset ${dur}s cubic-bezier(0.4,0,0.2,1) ${delay}s` : 'none',
    opacity: visible ? 1 : 0,
  });
  const fade = (delay: number): React.CSSProperties => ({
    opacity: visible ? 1 : 0,
    transition: visible ? `opacity 0.5s ease ${delay}s` : 'none',
  });

  // 6 petals: ellipse rotated 0,60,120,180,240,300 degrees
  const petals = [0, 60, 120, 180, 240, 300];

  return (
    <svg width="100%" viewBox="0 0 600 72" style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id="flo-lg" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="transparent" />
          <stop offset="8%"   stopColor={G}  stopOpacity="0.5" />
          <stop offset="40%"  stopColor={GB} />
          <stop offset="60%"  stopColor={GB} />
          <stop offset="92%"  stopColor={G}  stopOpacity="0.5" />
          <stop offset="100%" stopColor="transparent" />
        </linearGradient>
        <filter id="flo-glow">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="flo-strong">
          <feGaussianBlur stdDeviation="6" result="b" />
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Double lines — left */}
      <g filter="url(#flo-glow)" opacity="0.4">
        <path d="M15,31 H254" stroke={GB} strokeWidth="4" fill="none" style={draw(0, 1.3)} />
        <path d="M15,41 H254" stroke={GB} strokeWidth="4" fill="none" style={draw(0, 1.3)} />
      </g>
      <path d="M15,31 H254"  stroke="url(#flo-lg)" strokeWidth="1.8" fill="none" style={draw(0, 1.3)} />
      <path d="M15,41 H254"  stroke="url(#flo-lg)" strokeWidth="1.8" fill="none" style={draw(0.05, 1.3)} />

      {/* Double lines — right */}
      <g filter="url(#flo-glow)" opacity="0.4">
        <path d="M346,31 H585" stroke={GB} strokeWidth="4" fill="none" style={draw(0, 1.3)} />
        <path d="M346,41 H585" stroke={GB} strokeWidth="4" fill="none" style={draw(0, 1.3)} />
      </g>
      <path d="M346,31 H585" stroke="url(#flo-lg)" strokeWidth="1.8" fill="none" style={draw(0, 1.3)} />
      <path d="M346,41 H585" stroke="url(#flo-lg)" strokeWidth="1.8" fill="none" style={draw(0.05, 1.3)} />

      {/* Ornament buds on stems */}
      {([
        [230,36, 5,  1.2], [370,36, 5,  1.2],
        [180,36, 3.5,1.3], [420,36, 3.5,1.3],
        [130,36, 2.5,1.38],[470,36, 2.5,1.38],
        [80, 36, 2,  1.44],[520,36, 2,  1.44],
      ] as const).map(([cx, cy, r, d], i) => (
        <g key={i} filter={r > 4 ? "url(#flo-strong)" : "url(#flo-glow)"} style={fade(d)}>
          <circle cx={cx} cy={cy} r={r * 2.2} fill={GB} opacity="0.15">
            <animate attributeName="opacity" values="0.05;0.2;0.05" dur={`${2.5+i*0.4}s`} repeatCount="indefinite" />
          </circle>
          <circle cx={cx} cy={cy} r={r} fill={GB}>
            <animate attributeName="r" values={`${r*0.6};${r*1.4};${r*0.6}`} dur={`${2.5+i*0.4}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.5;1;0.5" dur={`${2.5+i*0.4}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}

      {/* 6 petals — drawn individually */}
      <g filter="url(#flo-glow)">
        {petals.map((angle, i) => (
          <ellipse
            key={i}
            cx="300"
            cy="22"
            rx="7"
            ry="14"
            stroke={i % 2 === 0 ? GB : G}
            strokeWidth={i % 2 === 0 ? "2" : "1.6"}
            fill="none"
            transform={`rotate(${angle} 300 36)`}
            style={draw(0.6 + i * 0.1, 0.9)}
          />
        ))}
      </g>

      {/* Outer flower ring */}
      <g filter="url(#flo-glow)">
        <circle cx="300" cy="36" r="24" stroke={GP} strokeWidth="1.2" fill="none" style={draw(1.2)} />
      </g>

      {/* Inner mandala — slowly rotating */}
      <g filter="url(#flo-glow)" style={fade(1.4)}>
        <g>
          <animateTransform attributeName="transform" type="rotate"
            from="0 300 36" to="360 300 36" dur="20s" repeatCount="indefinite" />
          <circle cx="300" cy="36" r="14" stroke={GB} strokeWidth="1" fill="none" strokeDasharray="6 5" />
          {[0,45,90,135,180,225,270,315].map((a, i) => {
            const rad = (a * Math.PI) / 180;
            const x = 300 + 14 * Math.sin(rad);
            const y = 36  - 14 * Math.cos(rad);
            return <circle key={i} cx={x} cy={y} r="1.5" fill={GB} opacity="0.8" />;
          })}
        </g>
      </g>

      {/* Center gem */}
      <g filter="url(#flo-strong)" style={fade(1.6)}>
        <circle cx="300" cy="36" r="6" fill={GB} opacity="0.3">
          <animate attributeName="r" values="4;9;4" dur="2.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.15;0.4;0.15" dur="2.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="300" cy="36" r="4.5" fill={GB}>
          <animate attributeName="r" values="3;5.5;3" dur="2.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="300" cy="36" r="2" fill="#FFFBE0" />
      </g>
    </svg>
  );
}

/* ─── Container ─────────────────────────────────────────────────── */
export default function SectionDivider({ variant = 'ornament' }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { setVisible(true); observer.unobserve(e.target); }
      }),
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="flex items-center justify-center py-12 px-4 sm:px-10 w-full max-w-3xl mx-auto overflow-x-clip"
    >
      {variant === 'ornament' && <OrnamentSVG visible={visible} />}
      {variant === 'branch'   && <BranchSVG  visible={visible} />}
      {variant === 'floral'   && <FloralSVG  visible={visible} />}
    </div>
  );
}
